import { useEffect, useState } from "react";
import { Excalidraw, restore } from "@excalidraw/excalidraw";
import { t } from "@excalidraw/excalidraw/i18n";

import type {
  AppState,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { ExcalidrawFrameElement } from "@excalidraw/element/types";
import type { BinaryFiles } from "@excalidraw/excalidraw/types";

import { PresentationScene } from "../presentation/Presentation";

import "./ShareViewer.scss";

const WORKER_URL = import.meta.env.VITE_APP_SHARE_WORKER_URL || "";

const RE_SHARE_LINK = /^#share(?:-pres)?=([A-Za-z0-9_-]+)$/;
const RE_SHARE_PRES_LINK = /^#share-pres=([A-Za-z0-9_-]+)$/;

export const isShareLink = (link: string) => {
  const hash = new URL(link).hash;
  return RE_SHARE_LINK.test(hash);
};

export const getShareIdFromLink = (link: string) => {
  const hash = new URL(link).hash;
  const match = hash.match(RE_SHARE_LINK);
  return match ? match[1] : null;
};

export const isSharePresentationLink = (link: string) => {
  const hash = new URL(link).hash;
  return RE_SHARE_PRES_LINK.test(hash);
};

type SceneData = {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
  files: BinaryFiles;
};

type LoadState =
  | { kind: "loading" }
  | { kind: "done"; scene: SceneData }
  | { kind: "error"; message: string };

const loadScene = async (shareId: string): Promise<SceneData> => {
  if (!WORKER_URL) {
    throw new Error(t("drawlab.share.errorNoBackend"));
  }
  const response = await fetch(`${WORKER_URL}/api/scene/${shareId}`);
  if (response.status === 404) {
    throw new Error(t("drawlab.share.errorExpired"));
  }
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const data = (await response.json()) as {
    type?: string;
    elements?: readonly ExcalidrawElement[];
    appState?: Partial<AppState>;
    files?: BinaryFiles;
  };
  if (data.type !== "excalidraw" || !Array.isArray(data.elements)) {
    throw new Error("Scene không hợp lệ");
  }
  const restored = restore(
    {
      elements: data.elements,
      appState: data.appState,
      files: data.files || {},
    },
    null,
    null,
  );
  return {
    elements: restored.elements,
    appState: restored.appState,
    files: restored.files,
  };
};

export const ShareViewer = ({
  shareId,
  presentation = false,
}: {
  shareId: string;
  presentation?: boolean;
}) => {
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    loadScene(shareId)
      .then((scene) => {
        if (!cancelled) {
          setState({ kind: "done", scene });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            kind: "error",
            message:
              err instanceof Error
                ? err.message
                : t("drawlab.share.errorUnknown"),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  const [excalidrawAPI, setExcalidrawAPI] =
    useState<ExcalidrawImperativeAPI | null>(null);
  // Scroll to content khi scene load xong.
  useEffect(() => {
    if (state.kind === "done" && excalidrawAPI) {
      excalidrawAPI.scrollToContent("fit", { animate: false });
    }
  }, [state, excalidrawAPI]);

  if (state.kind === "loading") {
    return (
      <div className="ShareViewer ShareViewer--loading">
        <div className="ShareViewer__spinner" />
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="ShareViewer ShareViewer--error">
        <div className="ShareViewer__errorCard">
          <p>{state.message}</p>
          <a className="ShareViewer__errorLink" href={window.location.pathname}>
            {t("drawlab.openApp")}
          </a>
        </div>
      </div>
    );
  }

  const { scene } = state;
  const frames = scene.elements.filter(
    (e): e is ExcalidrawFrameElement => e.type === "frame",
  );

  // Presentation mode (share có frame + hash #share-pres=).
  if (presentation && frames.length > 0 && scene.appState) {
    return (
      <PresentationScene
        appState={scene.appState as AppState}
        elements={[...scene.elements]}
        frames={frames}
        files={scene.files}
      />
    );
  }

  // Viewer tối giản: chỉ còn nội dung share. Ẩn hết chrome
  // (badge, zoom, menu, hand/lock, sidebar) qua UIOptions + viewModeEnabled + CSS.
  return (
    <div className="ShareViewer">
      <Excalidraw
        excalidrawAPI={setExcalidrawAPI}
        initialData={{
          elements: scene.elements as ExcalidrawElement[],
          appState: { ...scene.appState, name: "Shared scene" },
          files: scene.files,
          scrollToContent: true,
        }}
        viewModeEnabled
        UIOptions={{
          tools: { image: false },
          canvasActions: {
            loadScene: false,
            saveToActiveFile: false,
            toggleTheme: false,
            changeViewBackgroundColor: false,
          },
        }}
      />
    </div>
  );
};
